import type { CrudServiceListResponse, HttpService } from '@empathyco/playboard-core-domain'
import type { FacetsInfoAdapter, PlatformFacetsInfoEntity } from '../../adapter'
import type { FacetsInfoDto } from '../../models'

import type { ContextProvider } from '../../providers'

import type { FacetsInfoCrudService, FacetsInfoCrudServiceListParams } from '../interfaces'
import { interpolate } from '@empathyco/x-adapter'
import { inject, injectable, named, optional } from 'inversify'

import { TYPES } from '../../config/inversify.types'

@injectable()
export class PlatformFacetsInfoCrudService implements FacetsInfoCrudService {
  public constructor(
    @inject(TYPES.HttpService) protected httpService: HttpService<PlatformFacetsInfoEntity>,
    @inject(TYPES.FacetsInfoAdapter)
    protected adapter: FacetsInfoAdapter<PlatformFacetsInfoEntity | undefined>,
    @inject(TYPES.FacetsUrl) protected url: string,
    @inject(TYPES.ContextProvider) protected contextProvider: ContextProvider,
    @inject(TYPES.Endpoint)
    @named('facets-info')
    @optional()
    protected endpoint = '/facets-info/{instance}{(/)facet}{(/)id}',
  ) {}

  async list(
    params: FacetsInfoCrudServiceListParams,
  ): Promise<CrudServiceListResponse<FacetsInfoDto>> {
    try {
      const response = await this.httpService.get<{ results: Array<PlatformFacetsInfoEntity> }>(
        this.buildRequestUrl({ facet: params.facet }),
        {
          addAccessToken: true,
          parameters: {
            ...params.pivots.reduce<Record<string, string>>(
              (result, pivot) => ({ ...result, [pivot.name]: pivot.values[0] }),
              {},
            ),
            endpoint: params.endpoint,
            browseField: params.browseField,
            browseValue: params.browseValue,
            query: params.queries,
            inheritance: params.inheritance,
          },
        },
      )

      return {
        results: response.data!.results.map(result => this.adapter.decode(result)),
        numFound: response.data!.results.length,
      }
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async create(item: FacetsInfoDto): Promise<FacetsInfoDto> {
    try {
      const response = await this.httpService.post(this.buildRequestUrl(), {
        addAccessToken: true,
        body: this.adapter.encode(item),
      })
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async update(item: FacetsInfoDto): Promise<FacetsInfoDto> {
    try {
      const response = await this.httpService.put(this.buildRequestUrl({ id: item.id }), {
        addAccessToken: true,
        body: this.adapter.encode(item),
      })
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async delete(item: FacetsInfoDto): Promise<void> {
    try {
      await this.httpService.delete(this.buildRequestUrl({ id: item.id }), {
        addAccessToken: true,
      })
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async read(id: FacetsInfoDto['id']): Promise<FacetsInfoDto> {
    try {
      const response = await this.httpService.get(this.buildRequestUrl({ id }), {
        addAccessToken: true,
      })
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async readFacet(item: FacetsInfoDto): Promise<FacetsInfoDto> {
    try {
      const response = await this.httpService.get(
        this.buildRequestUrl({ id: item.id, facet: item.facet }),
        {
          addAccessToken: true,
        },
      )
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private buildRequestUrl(parameters: Record<string, any> = {}): string {
    const url = `${this.url}${this.endpoint}`
    const { instanceId: instance } = this.contextProvider()
    if (!instance) {
      throw new Error('instance is not defined')
    }
    return interpolate(url, { instance, ...parameters })
  }
}
